import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Button, Grid, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../components/Loader';
import ErrorScreen from '../../components/ErrorScreen';
import SearchField from '../../components/input/SearchField';
import Layout from '../../components/Layout/Layout';
import ModalCustom from '../../components/modals/ModalCustom';
import AddNew from './AddNew';
import Edit from './Edit';
import ModalConfirmation from '../../components/modals/ModalConfirmation';
import { apiRequest } from '../../utils/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchVendorData } from '../../redux/slices/vendorSlice';

export default function Vendors() {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loadingDelReq, setLoadingDelReq] = useState(false);

    const [vendor, setVendor] = useState(null);

    const [filteredData, setFilteredData] = useState(null);

    const dispatch = useDispatch();
    const { data, isLoading, error } = useSelector((state) => state.vendor);

    React.useEffect(() => {
        if (!data) {
            dispatch(fetchVendorData());
        } else {
            setFilteredData(data); // Initialize filtered data with all data
        }
    }, [data]);

    const handleSearch = (searchText) => {
        // Filter data based on search text
        const filteredResults = data.filter((row) => row?.name?.toLowerCase().includes(searchText?.toLowerCase()));

        setFilteredData(filteredResults);
    };

    const handleDelete = async () => {
        setLoadingDelReq(true);
        try {
            const res = await apiRequest('delete', `vendors/${vendor?.id}`, {});

            if (res?.data) {
                setVendor(null);
                dispatch(fetchVendorData());
                setShowDeleteModal(false);
                return toast.success('Deleted Successfully!');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoadingDelReq(false);
        }
    };

    return (
        <Layout>
            <>
                <Grid container sx={{ mb: 2 }} spacing={2} alignItems={'center'}>
                    <Grid item lg={7.5} sm={3.5} md={6.5} xs={6}>
                        <Typography variant="h6">Vendors</Typography>
                    </Grid>

                    <Grid item lg={1.5} sm={3.5} md={2.5} xs={6} sx={{ display: { xs: 'block', sm: 'none' } }}>
                        <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                            Create Vendor
                        </Button>
                    </Grid>

                    <Grid item lg={3} sm={5} md={3} xs={12}>
                        <SearchField onChange={(e) => handleSearch(e?.target?.value)} label={'Search Vendors'} />
                    </Grid>

                    <Grid item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                            Create Vendor
                        </Button>
                    </Grid>
                </Grid>
                {isLoading && <Loader />}

                {!!data && (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell sx={{ width: '60px' }} />
                                    <TableCell sx={{ width: '60px' }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData?.map((row) => (
                                    <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell>{row?.name}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => {
                                                    setVendor(row);
                                                    setShowEditModal(true);
                                                }}
                                                aria-label="Edit"
                                                size="small"
                                            >
                                                <BorderColorIcon sx={{ color: '#1976d2' }} />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => {
                                                    setVendor(row);
                                                    setShowDeleteModal(true);
                                                }}
                                                aria-label="Delete"
                                                size="small"
                                            >
                                                <DeleteIcon sx={{ color: 'red' }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {!!error && <ErrorScreen error={error} />}
            </>

            {showCreateModal && (
                <ModalCustom open={showCreateModal} title={'Create Vendor'}>
                    <AddNew onClose={() => setShowCreateModal(false)} />
                </ModalCustom>
            )}

            {showEditModal && (
                <ModalCustom open={showEditModal} title={'Edit Vendor'}>
                    <Edit vendor={vendor} onClose={() => setShowEditModal(false)} />
                </ModalCustom>
            )}

            <ModalConfirmation
                open={showDeleteModal}
                loading={loadingDelReq}
                header={'Delete Vendor'}
                description={`Are you sure, you want to delete ${vendor?.name}?`}
                onClickNo={() => setShowDeleteModal(false)}
                onClickYes={handleDelete}
            />
        </Layout>
    );
}
